/**
 * Aba Histórico salarial do funcionário:
 * - ESPELHO do histórico salarial do Convenia (E10e — somente leitura);
 * - H3 — TIMELINE DO EXTRATO (histórico congelado importado do Domínio):
 *   salário × líquido mês a mês, vigências cadastradas e SUGESTÕES de vigências
 *   faltantes (mudança de salário no extrato / divergência do cadastro) —
 *   proposta para revisão, NUNCA grava automático;
 * - H3 — FÉRIAS importadas (recibos com gozo/abono e líquido).
 */
import { useEffect, useState } from "react";
import { Alert, Badge, Card, Table, Text, Title } from "@mantine/core";
import { api } from "../../api";
import { fmtCompetencia, fmtData, fmtMoeda } from "../../format";
import { Carregando, Vazio } from "../../componentes/Estados";
import { TabelaDados, Th, TdNum } from "../../componentes/listas";

interface ItemHistorico {
  id: string;
  salarioCentavos: number;
  motivo: string | null;
  cargo: string | null;
  departamento: string | null;
  centroCusto: string | null;
  dateFrom: string;
  dateTo: string | null;
  isActiveConvenia: boolean;
  futuro: boolean;
}

interface MesExtrato {
  competencia: number;
  salarioCentavos: number | null;
  liquidoCentavos: number | null;
  situacao: string | null;
}

interface Sugestao {
  competencia: number;
  salarioCentavos: number;
  motivo: string;
  tipo: "mudanca_no_extrato" | "divergente_do_cadastro";
}

interface RespostaHistorico {
  historico: ItemHistorico[];
  extrato?: MesExtrato[];
  vigenciasCadastradas?: { vigenciaInicio: number; vigenciaFim: number | null; salarioBaseCentavos: number; motivoAlteracao: string | null }[];
  sugestoesVigencias?: Sugestao[];
}

interface FolhaImportada {
  competencia: number;
  liquidoCentavos: number | null;
  nomeArquivo: string;
}
interface FeriasImportada {
  id: string;
  competencia: number | null;
  gozoInicio: string | null;
  gozoDias: number | null;
  liquidoCentavos: number | null;
  nomeArquivo: string;
}

export function HistoricoSalarialTab({ funcionarioId }: { funcionarioId: string }) {
  const [dados, setDados] = useState<RespostaHistorico | null>(null);
  const [folhas, setFolhas] = useState<{ folhas: FolhaImportada[]; ferias: FeriasImportada[] } | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    api
      .get<RespostaHistorico>(`/funcionarios/${funcionarioId}/historico-salarial`)
      .then(setDados)
      .catch(setErro);
    api
      .get<{ folhas: FolhaImportada[]; ferias: FeriasImportada[] }>(`/funcionarios/${funcionarioId}/historico-folha`)
      .then(setFolhas)
      .catch(() => setFolhas({ folhas: [], ferias: [] }));
  }, [funcionarioId]);

  if (erro) return <Text c="red.7">{String(erro)}</Text>;
  if (!dados) return <Carregando linhas={4} />;

  const historico = dados.historico;
  const porMes = [...(dados.extrato ?? [])].sort((a, b) => b.competencia - a.competencia);
  const sugestoes = dados.sugestoesVigencias ?? [];
  const mesesComSugestao = new Map(sugestoes.map((s) => [s.competencia, s]));

  return (
    <>
      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Histórico salarial no Convenia <Badge variant="light">{historico.length}</Badge>
        </Title>
        {historico.length === 0 ? (
          <Vazio texto="Nenhum registro no espelho — rode o sync Convenia (domínio salarial) para esta pessoa." />
        ) : (
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Vigência</Th>
                <Th>Salário</Th>
                <Th>Motivo</Th>
                <Th>Cargo</Th>
                <Th>Departamento / CC</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {historico.map((h) => (
                <Table.Tr key={h.id}>
                  <Table.Td>
                    <Text size="sm">
                      {fmtData(h.dateFrom)} → {h.dateTo ? fmtData(h.dateTo) : "em vigor"}
                    </Text>
                    {h.futuro && (
                      <Badge size="sm" color="yellow" variant="light" ml={6}>
                        futuro
                      </Badge>
                    )}
                    {!h.dateTo && !h.futuro && (
                      <Badge size="sm" color="sillis" variant="light" ml={6}>
                        atual
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {fmtMoeda(h.salarioCentavos)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{h.motivo ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{h.cargo ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {[h.departamento, h.centroCusto].filter(Boolean).join(" · ") || "—"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
        <Text size="xs" c="dimmed" mt="sm">
          Espelho somente leitura do Convenia — as vigências que valem para o cálculo da folha são as da aba Contrato.
        </Text>
      </Card>

      {sugestoes.length > 0 && (
        <Alert color="yellow" variant="light" mb="md" title="Sugestões de vigências (histórico importado — revise antes de cadastrar)">
          {sugestoes.map((s, i) => (
            <Text key={i} size="xs">
              • <strong>{fmtCompetencia(s.competencia)}</strong> — {s.motivo}
            </Text>
          ))}
        </Alert>
      )}

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Linha do tempo no extrato (histórico importado) <Badge variant="light">{porMes.length} meses</Badge>
        </Title>
        {porMes.length === 0 ? (
          <Vazio texto="Nenhum mês importado para esta pessoa — rode a importação do histórico (H1/H2)." />
        ) : (
          <TabelaDados striped>
            <Table.Thead>
              <Table.Tr>
                <Th>Competência</Th>
                <Th>Salário (extrato)</Th>
                <Th>Líquido</Th>
                <Th>Situação</Th>
                <Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {porMes.map((m) => {
                const sug = mesesComSugestao.get(m.competencia);
                return (
                  <Table.Tr key={m.competencia}>
                    <Table.Td>
                      <Text size="sm">{fmtCompetencia(m.competencia)}</Text>
                    </Table.Td>
                    <TdNum>
                      <Text size="sm" fw={600}>
                        {m.salarioCentavos !== null ? fmtMoeda(m.salarioCentavos) : "—"}
                      </Text>
                    </TdNum>
                    <TdNum>
                      <Text size="sm">{m.liquidoCentavos !== null ? fmtMoeda(m.liquidoCentavos) : "—"}</Text>
                    </TdNum>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {m.situacao ?? "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {sug && (
                        <Badge size="sm" color={sug.tipo === "mudanca_no_extrato" ? "blue" : "yellow"} variant="light" title={sug.motivo}>
                          {sug.tipo === "mudanca_no_extrato" ? "mudança" : "≠ cadastro"}
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </TabelaDados>
        )}
        <Text size="xs" c="dimmed" mt="sm">
          Salário e líquido conforme o Extrato Mensal importado (PDF do retorno Contmega — dado congelado).
        </Text>
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Férias importadas (recibos do histórico) <Badge variant="light">{folhas?.ferias.length ?? 0}</Badge>
        </Title>
        {!folhas || folhas.ferias.length === 0 ? (
          <Vazio texto="Nenhum recibo de férias importado para esta pessoa." />
        ) : (
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Gozo</Th>
                <Th>Dias</Th>
                <Th>Líquido do recibo</Th>
                <Th>Arquivo</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {folhas.ferias.map((f) => (
                <Table.Tr key={f.id}>
                  <Table.Td>
                    <Text size="sm">{f.gozoInicio ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{f.gozoDias ?? "—"}</Text>
                  </Table.Td>
                  <TdNum>
                    <Text size="sm">{f.liquidoCentavos !== null ? fmtMoeda(f.liquidoCentavos) : "—"}</Text>
                  </TdNum>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {f.nomeArquivo}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>
    </>
  );
}
